// AuthContext for managing authentication state and redirecting users
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import JwtService from "./services/jwtService";

interface User {
  uuid: string;
  username: string;
  email: string;
  userRole: string;
  isOperator: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
  signInWithToken: () => Promise<any>;
  setAuthenticationState: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if admin is already logged in on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const accessToken = localStorage.getItem("admin_access_token");
        const refreshToken = localStorage.getItem("admin_refresh_token");

        if (accessToken && refreshToken) {
          // Check if we have stored admin user data
          const adminUserData = localStorage.getItem("adminUserData");
          if (adminUserData) {
            const userData = JSON.parse(adminUserData);
            setUser(userData as User);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        // Token is invalid, clear storage
        localStorage.removeItem("admin_access_token");
        localStorage.removeItem("admin_refresh_token");
        localStorage.removeItem("adminUserData");
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string) => {
    // This function is not used in admin app, admin login is handled by useAdminLogin hook
    // But keeping it for interface compatibility
    throw new Error("Use useAdminLogin hook for admin authentication");
  };

  const logout = () => {
    localStorage.removeItem("admin_access_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("adminUserData");
    setIsAuthenticated(false);
    setUser(null);
  };

  const signInWithToken = async () => {
    try {
      const userData = await JwtService.signInWithToken();
      setUser(userData as User);
      setIsAuthenticated(true);
      return userData;
    } catch (error) {
      logout();
      throw error;
    }
  };

  const setAuthenticationState = (userData: User) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        signInWithToken,
        setAuthenticationState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
